import { type FC, useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { vipBenefitsConfig } from '@/common/config/vip-benefirs';
import SelectPriceCard from '@/components/common/SelectPriceCard/SelectVipPriceCard';
import {
  H1,
  StringMain,
  StringSmall,
} from '@/components/Typography/Typography';
import { MainButton } from '@/components/ui/Button/Button';
import { Close } from '@/components/ui/Close/Close';
import { Picture } from '@/components/ui/Picture/Picture';
import { useVipPlans } from '@/components/Vip/useVipPlans';

import { ModalContext } from '../Modal/config';

import { Background } from './Background/Background';
import { EndPeriodBackground } from './Background/EndPeriodBackground';
import SelectVipBenefit from './SelectVipBenefit/SelectVipBenefit';

import styles from './styles.module.scss';

import LightPng from '/img/circle-light-effect.png';
import LightWebp from '/img/circle-light-effect.webp';
import CrownPng from '/img/VIP/crown-3d.png';
import CrownWebp from '/img/VIP/crown-3d.webp';

const SelectVipPriceModal: FC<{ type: 'paid' | 'endPeriod' }> = ({ type }) => {
  const { closeModal } = useContext(ModalContext);
  const { t } = useTranslation();
  const {
    plans: paidVipOffers,
    selectedIndex: selectedPriceIndex,
    setSelectedIndex: setSelectedPriceIndex,
    selected: selectedPrice,
    sales,
    getPriceByDiscount,
    storePrice,
    buy: buyPaidVip,
    isAppStore,
  } = useVipPlans();

  return (
    <div className="relative w-full pt-7 px-4">
      {type === 'endPeriod' ? <EndPeriodBackground /> : <Background />}
      <Close
        onClick={closeModal}
        className={'absolute top-4 right-4 z-10'}
        imageVariant="outline"
      />
      <div className="w-full flex flex-col items-center relative z-[2] min-h-80">
        <H1
          className={classNames(
            'font-bold',
            styles.h1,
            type === 'endPeriod' && 'text-center mb-4 mt-5',
          )}
        >
          <Trans
            i18nKey={
              type === 'endPeriod'
                ? 'VIP subscription <1/> has expired'
                : 'VIP CLUB'
            }
            components={{ 1: <br /> }}
          />
        </H1>

        {type !== 'endPeriod' && (
          <div className="relative mb-1 mt-2.5 size-[6.75rem] flex items-center justify-center">
            <Picture
              alt="light"
              img={LightPng}
              webp={LightWebp}
              className="w-[18.25rem] mt-3 absolute mix-blend-overlay -z-10 opacity-20"
            />
            <Picture
              alt="vip"
              img={CrownPng}
              webp={CrownWebp}
              className="-m-3"
            />
          </div>
        )}
        <StringSmall
          className={classNames(
            '!text-white/80 text-center max-w-[19.5rem]',
            type === 'endPeriod' && 'mb-10 !leading-1.2',
          )}
        >
          <Trans
            i18nKey={
              type === 'endPeriod'
                ? 'Want to keep your benefit <1/> sand stay ahead?'
                : 'Get more features, speed up your progress, and enjoy exclusive privileges'
            }
            components={{ 1: <br /> }}
          />
        </StringSmall>

        <ul
          className={classNames(
            'flex flex-col gap-1 self-stretch',
            type !== 'endPeriod' && 'mt-3',
          )}
        >
          {[6, 0, 4, 5, 3].map((i) => (
            <SelectVipBenefit
              key={i}
              // notAvailable={i === 3 && type === 'paid'}
              icon={vipBenefitsConfig.shopIcons[i]}
              transKey={vipBenefitsConfig.shopTransKeyObjBenefits[i](t)}
            />
          ))}
        </ul>

        <div className="grid grid-cols-[6.75rem_auto_6.75rem] gap-4 items-center my-5">
          <div className={styles['gradient-line']} />
          <StringMain className="uppercase !font-bold">
            {t('Choose a period')}
          </StringMain>
          <div className={styles['gradient-line']} />
        </div>

        <div className="grid grid-cols-4 gap-1 self-stretch">
          {paidVipOffers.map((price, i) => (
            <SelectPriceCard
              {...price}
              key={i}
              price={getPriceByDiscount(i)}
              active={selectedPriceIndex === i}
              onClick={() => setSelectedPriceIndex(i)}
              sale={sales[i]}
              type="usdt"
              variant="transparent"
              appleVipDays={isAppStore ? price.days : undefined}
            />
          ))}
        </div>

        <MainButton
          onClick={buyPaidVip}
          className="mt-5"
          text={`${t('Buy for')} ${storePrice ?? `$ ${selectedPrice?.discountPrice}`}`}
        />
      </div>
    </div>
  );
};

export default SelectVipPriceModal;
